import { createSelector } from 'reselect';

import type { AdditionalSelectors, Dealership } from '@makemydeal/dr-activities-change-vehicle';
import type { IVehicle } from '@makemydeal/dr-platform-types';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { getActiveDeal, getAvailableDealerships, getCommonOrgId, getDealerDetails } from '@makemydeal/dr-sales-checkin-store';

import { getServicesBff } from '../../selectors/salesCheckinAppSelectors';

const toCondition = (inventoryType?: string) =>
    inventoryType === 'CertifiedPreOwned' ? 'certified' : inventoryType?.toLowerCase();

const getDealVehicle = createSelector([(state) => getActiveDeal(state)], (activeDeal): IVehicle | undefined => {
    const dealStructure = activeDeal?.dealStructure;
    const vehicle = dealStructure?.vehicles?.sellingVehicle;
    if (!dealStructure || !vehicle) {
        return undefined;
    }
    const priceDetails = dealStructure.financialDetails?.priceDetails;
    return {
        vin: vehicle.vin,
        stockNumber: vehicle.stockNumber,
        condition: toCondition(vehicle.inventoryType),
        mileage: vehicle.odometerMileage,
        retailPrice: priceDetails?.retailPrice,
        listedPrice: priceDetails?.sellingPrice
    } as IVehicle;
});

const getChangeVehicleDealerships = createSelector(
    [
        (state) => featureToggleSelectors.isEnableCrossDealershipVehicleSearchSCI(state),
        (state) => featureToggleSelectors.isEnableCrossDealershipAIVehicleSearchSCI(state),
        (state) => getAvailableDealerships(state)
    ],
    (isCrossDealershipSearchEnabled, isCrossDealershipAISearchEnabled, availableDealerships): Dealership[] | undefined => {
        return isCrossDealershipSearchEnabled || isCrossDealershipAISearchEnabled ? availableDealerships : undefined;
    }
);

export const additionalSelectors: AdditionalSelectors = {
    getCommonOrgId: (state) => getCommonOrgId(state),
    getDealerName: (state) => getDealerDetails(state)?.dealerName || '',
    getDealId: (state) => getActiveDeal(state)?.dealId,
    // Lets getFilteredAISearchResults drop the deal's current vehicle out of AI Search results instead of
    // returning the raw response unfiltered. Gated on AI search being enabled: getInitialSearchParameters
    // below seeds the classic search box with the deal's own VIN when AI is off, so that search's only hit
    // is the current vehicle — if these selectors were unconditional, getFilteredSearchResults would strip
    // that hit and leave non-AI dealers looking at an empty "This is the current vehicle" error on load.
    getVehicleVin: (state) =>
        dealerSelectors.isDealerAISearchEnabled(state)
            ? getActiveDeal(state)?.dealStructure?.vehicles?.sellingVehicle?.vin
            : undefined,
    getVehicleStockNumber: (state) =>
        dealerSelectors.isDealerAISearchEnabled(state)
            ? getActiveDeal(state)?.dealStructure?.vehicles?.sellingVehicle?.stockNumber
            : undefined,
    getServicesBff,
    getInitialSearchParameters(state) {
        // In AI mode the search auto-runs a similarity search on the base vehicle, so don't
        // pre-seed the search box with the VIN (a seeded search suppresses that auto-trigger).
        if (dealerSelectors.isDealerAISearchEnabled(state)) {
            return {
                search: ''
            };
        }
        const vin = getActiveDeal(state)?.dealStructure?.vehicles?.sellingVehicle?.vin || '';
        return {
            search: vin
        };
    },
    getInitialPageSize: () => 6,
    getDealerships: getChangeVehicleDealerships,
    isEnableCrossDealershipAIVehicleSearch: featureToggleSelectors.isEnableCrossDealershipAIVehicleSearchSCI,
    isAgeOfInventorySearchEnabled: featureToggleSelectors.isAgeOfInventorySearchEnabledSCI,
    isPromptOptimizationEnabled: featureToggleSelectors.enablePromptOptimization,
    isSimilarityBodyStyleRelaxationEnabled: featureToggleSelectors.isSimilarityBodyStyleRelaxationEnabled,
    // SCI deals may not have a vehicle yet; fall back to showing all inventory
    showAllVehiclesIfNoBaseVehicle: () => true,
    // AVO (section + Add New Scenario) is Manager View-only. Pin false explicitly so the host-scope
    // decision is visible here and survives F291824 toggle removal — do not rely on omission.
    isAiAltVehicleOptionsActive: () => false,
    isAiAltVehicleOptionsSectionVisible: () => false,
    // No scoped store outside Manager View, so a scenario cannot be created here.
    canCreateScenario: () => false,
    isAutoExpandDealVehicleEnabled: featureToggleSelectors.isAutoExpandDealVehicleEnabledSCI,
    getDealVehicle
};
